import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Icon,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { FieldArray, Field, useFormikContext } from "formik";
import LGItemCellHeader from "../../atoms/Common/LGItemCellHeader";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import { formatCurrency } from "../../services/valueFormatters";
import dayjs from "dayjs";

export const createEmptyRaPayment = (installmentNo = 1, amount = null, paymentDate = "") => ({
  installmentNo,
  paymentConditionId: null,
  paymentDate,
  amount,
});

export const computeDefaultPaymentDate = (invoiceDate, creditDays = 30) => {
  const date = dayjs(invoiceDate);
  if (!date.isValid()) return "";

  const dateWithCredit = date.add(creditDays || 30, "day");
  const finalDate =
    dateWithCredit.day() <= 3
      ? dateWithCredit.day(3)
      : dateWithCredit.add(1, "week").day(3);

  return finalDate.format("YYYY-MM-DD");
};

const RAPayments = ({ formMode }) => {
  const { values, setFieldValue } = useFormikContext();

  const total = parseFloat(values.receiptAcknowledgement?.total) || 0;
  const payments = values.raPayments || [];

  useEffect(() => {
    if (formMode !== "insert") return;
    if (!payments.length) return;

    const creditDays = values.selectedProvider?.creditDays || 30;
    const nextDate = computeDefaultPaymentDate(
      values.receiptAcknowledgement?.invoiceDate,
      creditDays
    );
    const firstPayment = payments[0];

    if (nextDate && firstPayment.paymentDate !== nextDate) {
      setFieldValue("raPayments.0.paymentDate", nextDate);
    }
  }, [
    formMode,
    values.selectedProvider,
    values.receiptAcknowledgement?.invoiceDate,
  ]);

  useEffect(() => {
    if (formMode !== "insert") return;
    if (payments.length !== 1) return;
    const nextAmount = total || null;
    const currentAmount = payments[0]?.amount ?? null;
    if (Number(currentAmount) === Number(nextAmount) || currentAmount === nextAmount) {
      return;
    }
    setFieldValue("raPayments.0.amount", nextAmount);
  }, [formMode, total, payments.length, setFieldValue]);

  return (
    <Grid container>
      <Grid item xs={1}>
        <LGItemCellHeader label={"No."} />
      </Grid>
      <Grid item xs={4}>
        <LGItemCellHeader label={"Fecha de pago"} />
      </Grid>
      <Grid item xs={6}>
        <LGItemCellHeader label={"Monto"} />
      </Grid>
      <Grid item xs={1}>
        <LGItemCellHeader label={"Eliminar"} color={"error.main"} />
      </Grid>

      <FieldArray name="raPayments">
        {({ push, remove, form }) => {
          const rows = form.values.raPayments || [];
          const allocated = rows.reduce(
            (sum, payment) => sum + (parseFloat(payment.amount) || 0),
            0
          );
          const remaining = total - allocated;
          const isBalanced = Math.abs(remaining) < 0.01;

          return (
            <Grid item xs={12}>
              {rows.map((payment, index) => {
                return (
                  <Grid
                    container
                    item
                    xs={12}
                    key={payment.id ?? index}
                    alignItems="center"
                  >
                    <Grid item xs={1}>
                      <Typography sx={{ ml: 1 }} variant="body2">
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Field name={`raPayments.${index}.paymentDate`}>
                        {({ field }) => (
                          <TextField
                            fullWidth
                            //label="Fecha de pago"
                            type="date"
                            {...field}
                            value={field.value ?? ""}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={6}>
                      <Field name={`raPayments.${index}.amount`}>
                        {({ field }) => (
                          <LGNumber
                            fullWidth
                            {...field}
                            variant="outlined"
                            decimals={4}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        onClick={() => remove(index)}
                        color="error"
                        disabled={rows.length === 1}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() =>
                    push(
                      createEmptyRaPayment(
                        rows.length + 1,
                        remaining > 0 ? Number(remaining.toFixed(4)) : null,
                        ""
                      )
                    )
                  }
                  color="primary"
                >
                  <Icon>add</Icon>
                </Button>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Asignado: {formatCurrency(allocated)} / Total:{" "}
                  {formatCurrency(total)}
                </Typography>
                <Typography
                  variant="body2"
                  color={isBalanced ? "success.main" : "error"}
                >
                  {isBalanced
                    ? "Las parcialidades cubren el total del contrarrecibo."
                    : `Pendiente por asignar: ${formatCurrency(remaining)}`}
                </Typography>
              </Box>
            </Grid>
          );
        }}
      </FieldArray>
    </Grid>
  );
};

export default RAPayments;
